<template>
    <div class="container">

        <div v-if="isUploading">

            <div class="h3 mt-4">
                Upload in progress
            </div>

            <div class="progress-bar mt-4" role="progressbar" v-bind:style="{ width: uploadPercentCompleted + '%' }" :aria-valuenow="uploadPercentCompleted" aria-valuemin="0" aria-valuemax="100">
                {{uploadPercentCompleted}}%
            </div>

        </div>

        <div v-if="!isUploading">

            <button v-on:click="toggleRecording" class="btn btn-block mb-2" :class="isRecording ? 'btn-primary' : 'btn-danger'" v-if="recordings.length < maxTries">
                {{ (isRecording) ? 'Stop recording' : 'Record your answer' }} ( Time remaining {{getRemainingDuration}} )
            </button>

            <div class="h3" v-if="recordings.length >= maxTries">
                Please remove one recording or more if you want to record a new answer.
            </div>

            <canvas ref="audioCanvas" width="100px" :height="(isRecording) ? '100px': '0px'">

            </canvas>

            <div v-for="(audioSrc, index) in recordings" class="card mt-4">

                <div class="card-header">
                    Recording ( {{ index + 1 }} / {{ maxTries }} )
                </div>

                <div class="card-body">

                    <div class="row">
                        <div class="col-12 col-lg-6 p-2">
                            <audio :src="audioSrc" controls style="width: 100%; height: 100%"></audio>
                        </div>

                        <div class="col-12 col-md-6 col-lg-3 p-2">
                            <button v-on:click="uploadRecording(index)" class="btn btn-success btn-block btn-lg" style="height: 100%">
                                Submit <span class="remixicon-upload-line ri-xl"></span>
                            </button>
                        </div>

                        <div class="col-12 col-md-6 col-lg-3 p-2">
                            <button v-on:click="deleteRecording(index)" class="btn btn-warning btn-block btn-lg" style="height: 100%">
                                Delete <span class="remixicon-delete-bin-line ri-xl"></span>
                            </button>
                        </div>
                    </div>

                </div>

            </div>

        </div>

    </div>

</template>

<script>
    export default {

        props: [
            'upload-url',
            'tries-url',
            'min-duration',
            'max-duration',
            'max-tries',
        ],

        data() {
            return {
                isRecording: false,

                audioInput: null,
                audioNode: null,
                audioCtx: null,

                bufferSize: 4096,

                recordingData: [],
                recordingLength: 0,
                recordingDuration: 0,
                recordingStartDate: null,

                recordings: [],
                recordingsBlobs: [],

                uploadPercentCompleted: 10,
                isUploading: false,
            }
        },

        beforeDestroy: function () {
            this.recordings = [];
            this.resetRecording();
        },

        computed: {
            getRemainingDuration() {

                let remaining = 0;

                if (this.isRecording) {
                    remaining = (this.maxDuration * 1000) - this.recordingDuration;
                } else {
                    remaining = (this.maxDuration * 1000);
                }

                if (remaining <= 0) {
                    remaining = 0;
                }

                let minutes = Math.floor(remaining / 60000);
                let seconds = ((remaining % 60000) / 1000).toFixed(0);
                return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
            }
        },

        watch: {
            recordingDuration: function (duration) {
                if ((duration / 1000).toFixed(0) > parseInt(this.maxDuration)) {
                    this.stopRecording();
                }
            }
        },

        methods: {

            toggleRecording() {
                this.isRecording ? this.stopRecording() : this.startRecording();
            },

            startRecording() {

                this.resetRecording();

                // webkit audio context shim
                this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

                if (this.audioCtx.createJavaScriptNode) {
                    this.audioNode = this.audioCtx.createJavaScriptNode(this.bufferSize, 1, 1);
                } else if (this.audioCtx.createScriptProcessor) {
                    this.audioNode = this.audioCtx.createScriptProcessor(this.bufferSize, 1, 1);
                } else {
                    throw 'WebAudio not supported!';
                }

                this.audioNode.connect(this.audioCtx.destination);

                navigator.mediaDevices.getUserMedia({audio: true})
                    .then(this.onMicrophoneCaptured)
                    .catch(this.onMicrophoneError);
            },

            stopRecording() {
                // stop recording
                this.isRecording = false;

                // to make sure onaudioprocess stops firing
                window.localStream.getTracks().forEach((track) => {
                    track.stop();
                });

                this.audioInput.disconnect();

                this.audioNode.disconnect();

                this.exportToWav({
                    sampleRate: this.sampleRate,
                    recordingLength: this.recordingLength,
                    data: this.recordingData
                }, (buffer, view) => {

                    let blob = new Blob([view], {type: 'audio/wav'});

                    this.recordings.push(URL.createObjectURL(blob));
                    this.recordingsBlobs.push(blob);
                });

            },

            resetRecording() {
                this.recordingData = [];
                this.recordingLength = 0;
            },

            deleteRecording(index) {
                if (index > -1) {
                    URL.revokeObjectURL(this.recordings[index]);
                    this.recordings.splice(index, 1);
                    this.recordingsBlobs.splice(index, 1);
                }
            },

            uploadRecording(index) {

                this.isUploading = true;

                let data = new FormData();

                data.append('audio', this.recordingsBlobs[index]);

                window.axios.post(this.uploadUrl, data, {
                    headers: {
                        'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
                    },
                    onUploadProgress: (progressEvent) => {
                        this.uploadPercentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                }).then(res => {
                    location.reload();
                }).catch(err => {
                    console.log(err);
                    this.isUploading = false;
                });

            },

            ////////////////////////// Events //////////////////////////

            onRecordingStart() {

                window.axios.put(this.triesUrl)
                    .then(res => {
                        console.log(res);
                    })
                    .catch(err => {
                        console.log(err);
                    });

            },

            onAudioProcess(e) {
                if (!this.isRecording) {
                    return;
                }

                this.recordingData.push(new Float32Array(e.inputBuffer.getChannelData(0)));

                this.recordingLength += this.bufferSize;

                this.recordingDuration = new Date().getTime() - this.recordingStartDate.getTime();
            },

            onMicrophoneCaptured(microphone) {

                this.visualizeAudio(microphone);

                // save the stream so we can disconnect it when we're done
                window.localStream = microphone;

                this.audioInput = this.audioCtx.createMediaStreamSource(microphone);
                this.audioInput.connect(this.audioNode);

                this.audioNode.onaudioprocess = this.onAudioProcess;

                this.isRecording = true;

                this.recordingStartDate = new Date();

                this.onRecordingStart();

                this.sampleRate = this.audioCtx.sampleRate;
            },

            onMicrophoneError(e) {
                console.log(e);
                alert('Unable to access the microphone.');
            },

            ////////////////////////// Core //////////////////////////

            visualizeAudio(stream) {

                let canvas = this.$refs.audioCanvas;

                if (!canvas)
                    return;

                // Make it visually fill the positioned parent
                canvas.style.width = '100%';
                canvas.style.height = '20%';

                // ...then set the internal size to match
                canvas.width = canvas.offsetWidth;
                canvas.height = canvas.offsetHeight;

                let canvasCtx = canvas.getContext("2d");

                let source = this.audioCtx.createMediaStreamSource(stream);

                let analyser = this.audioCtx.createAnalyser();
                analyser.fftSize = 512;

                let bufferLength = analyser.frequencyBinCount;
                let dataArray = new Uint8Array(bufferLength);

                source.connect(analyser);

                let draw = () => {
                    // get the canvas dimensions
                    let width = canvas.width, height = canvas.height;

                    // ask the browser to schedule a redraw before the next repaint
                    requestAnimationFrame(draw);

                    // clear the canvas
                    canvasCtx.fillStyle = '#fff';
                    canvasCtx.fillRect(0, 0, width, height);

                    if (!this.isRecording)
                        return;

                    canvasCtx.lineWidth = 2;
                    canvasCtx.strokeStyle = '#f00';

                    canvasCtx.beginPath();

                    let sliceWidth = width / bufferLength;
                    let x = 0;

                    analyser.getByteTimeDomainData(dataArray);

                    for (let i = 0; i < bufferLength; i++) {

                        let v = dataArray[i] / 128.0;
                        let y = v * height / 2;

                        i === 0 ? canvasCtx.moveTo(x, y) : canvasCtx.lineTo(x, y);
                        x += sliceWidth;
                    }

                    canvasCtx.lineTo(canvas.width, canvas.height / 2);
                    canvasCtx.stroke();
                };

                draw();
            },

            exportToWav(config, callback) {

                function inlineWebWorker(config, cb) {

                    let data = config.data.slice(0);

                    let sampleRate = config.sampleRate;

                    data = joinBuffers(data, config.recordingLength);

                    function joinBuffers(channelBuffer, count) {
                        let result = new Float64Array(count);
                        let offset = 0;
                        let lng = channelBuffer.length;

                        for (let i = 0; i < lng; i++) {
                            let buffer = channelBuffer[i];
                            result.set(buffer, offset);
                            offset += buffer.length;
                        }

                        return result;
                    }

                    function writeUTFBytes(view, offset, string) {
                        let lng = string.length;
                        for (let i = 0; i < lng; i++) {
                            view.setUint8(offset + i, string.charCodeAt(i));
                        }
                    }

                    let dataLength = data.length;

                    // create wav file
                    let buffer = new ArrayBuffer(44 + dataLength * 2);
                    let view = new DataView(buffer);

                    writeUTFBytes(view, 0, 'RIFF'); // RIFF chunk descriptor/identifier
                    view.setUint32(4, 44 + dataLength * 2, true); // RIFF chunk length
                    writeUTFBytes(view, 8, 'WAVE'); // RIFF type
                    writeUTFBytes(view, 12, 'fmt '); // format chunk identifier, FMT sub-chunk
                    view.setUint32(16, 16, true); // format chunk length
                    view.setUint16(20, 1, true); // sample format (raw)
                    view.setUint16(22, 1, true); // mono (1 channel)
                    view.setUint32(24, sampleRate, true); // sample rate
                    view.setUint32(28, sampleRate * 2, true); // byte rate (sample rate * block align)
                    view.setUint16(32, 2, true); // block align (channel count * bytes per sample)
                    view.setUint16(34, 16, true); // bits per sample
                    writeUTFBytes(view, 36, 'data'); // data sub-chunk identifier
                    view.setUint32(40, dataLength * 2, true); // data chunk length

                    // write the PCM samples
                    let index = 44;
                    for (let i = 0; i < dataLength; i++) {
                        view.setInt16(index, data[i] * 0x7FFF, true);
                        index += 2;
                    }

                    if (cb) {
                        return cb({
                            buffer: buffer,
                            view: view
                        });
                    }

                    postMessage({
                        buffer: buffer,
                        view: view
                    });
                }

                let webWorker = this.processInWebWorker(inlineWebWorker);

                webWorker.onmessage = function (event) {
                    callback(event.data.buffer, event.data.view);

                    // release memory
                    URL.revokeObjectURL(webWorker.workerURL);
                };

                webWorker.postMessage(config);
            },

            processInWebWorker(_function) {

                let workerURL = URL.createObjectURL(new Blob([_function.toString(),
                    ';this.onmessage = function (e) {' + _function.name + '(e.data);}'
                ], {
                    type: 'application/javascript'
                }));

                let worker = new Worker(workerURL);

                worker.workerURL = workerURL;

                return worker;

            }

        },

    }
</script>
